import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, type ImportPreview, type ImportResult, type Mapping } from "../lib/api.js";
import { useSession } from "../lib/session.js";

/**
 * Bringing the family's calendar in from Skylight.
 *
 * The export tool produces a file; this reads it, shows what would happen, and
 * writes it. The only real decision is what each Skylight *category* becomes,
 * because Skylight's category list mixes real people with a synced account's
 * address, a holiday feed and a cleaning rota. Frankie proposes and this screen
 * lets that be corrected — nothing is written until the Import button.
 *
 * It is deliberately forgiving rather than strict. Bringing everything across
 * and tidying up in the calendar afterwards is faster than getting a mapping
 * perfect in the abstract, and the import can be re-run or removed outright, so
 * a wrong choice costs a minute rather than an evening.
 */

const SKIP = "__skip__";

/** The Skylight sources, in the order they are worth explaining. */
const SOURCE_LABELS: Record<string, string> = {
  skylight: "Typed into Skylight — the ones that exist nowhere else",
  google: "Synced from Google — these already live in Google Calendar",
  ics_link: "A subscribed feed, like US holidays",
};

interface ExportShape {
  events: unknown[];
}

/**
 * Everything the server needs and nothing it doesn't.
 *
 * The export keeps every original Skylight attribute so a misreading stays
 * recoverable, which makes the file several times larger than the import cares
 * about. Sending the whole thing would work and would also mean uploading a
 * decade of raw JSON to answer "who is Svetlana".
 */
function trim(events: unknown[]): unknown[] {
  return events.map((raw) => {
    const event = raw as Record<string, unknown>;
    const skylight = (event["skylight"] ?? {}) as Record<string, unknown>;
    const categories = (skylight["categories"] ?? []) as Record<string, unknown>[];
    return {
      title: event["title"] ?? null,
      starts_at: event["starts_at"] ?? null,
      ends_at: event["ends_at"] ?? null,
      ends_on: event["ends_on"] ?? null,
      all_day: event["all_day"] === true,
      location: event["location"] ?? null,
      notes: event["notes"] ?? null,
      rrule: event["rrule"] ?? null,
      source: event["source"] ?? null,
      skylight: {
        uid: skylight["uid"] ?? null,
        id: skylight["id"] ?? null,
        categories: categories.map((category) => ({
          label: (category["label"] ?? null) as string | null,
        })),
      },
    };
  });
}

export function ImportSkylight(): React.JSX.Element {
  const { people, canEdit } = useSession();

  const events = useRef<unknown[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const names = people.map((person) => person.display_name);

  /*
   * The mapping this screen has already had planned. Without it the effect
   * below re-plans its own answer forever: planning sets state, state fires the
   * effect, the effect plans again.
   */
  const planned = useRef<Mapping | null>(null);

  const refresh = useCallback(async (next: Mapping | null) => {
    if (events.current === null) return;
    setBusy(true);
    setError(null);
    try {
      const body = await api.importPreview(events.current, next);
      setPreview(body);
      /*
       * Adopt the server's proposal only when there was nothing to propose
       * against. Echoing the mapping back on every re-plan would replace the
       * object being edited and lose whatever was typed in the meantime.
       */
      if (next === null) {
        planned.current = body.mapping;
        setMapping(body.mapping);
      }
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }, []);

  /*
   * Re-planning means re-sending the export, so it waits for a pause rather
   * than firing on every click of a dropdown.
   */
  useEffect(() => {
    if (mapping === null || mapping === planned.current) return;
    const timer = setTimeout(() => {
      planned.current = mapping;
      void refresh(mapping);
    }, 350);
    return () => clearTimeout(timer);
  }, [mapping, refresh]);

  async function chooseFile(file: File): Promise<void> {
    setResult(null);
    setError(null);
    setFileName(file.name);
    try {
      const parsed = JSON.parse(await file.text()) as ExportShape;
      if (!Array.isArray(parsed.events)) {
        setError("That file has no events in it. Pick skylight-events.json.");
        return;
      }
      events.current = trim(parsed.events);
      await refresh(null);
    } catch {
      setError("That file isn't readable JSON. Pick skylight-events.json from the export.");
    }
  }

  function setRule(label: string, choice: string): void {
    if (mapping === null) return;
    const previous = mapping.categories[label] ?? {};
    const rule =
      choice === SKIP
        ? { skip: true, note: previous.note }
        : {
            person: choice,
            background: previous.background,
            person_from_title: previous.person_from_title,
            note: previous.note,
          };
    setMapping({ ...mapping, categories: { ...mapping.categories, [label]: rule } });
  }

  function toggleBackground(label: string): void {
    if (mapping === null) return;
    const previous = mapping.categories[label] ?? {};
    setMapping({
      ...mapping,
      categories: {
        ...mapping.categories,
        [label]: { ...previous, background: previous.background !== true },
      },
    });
  }

  function toggleSource(source: string): void {
    if (mapping === null) return;
    const on = mapping.sources.includes(source);
    setMapping({
      ...mapping,
      sources: on
        ? mapping.sources.filter((name) => name !== source)
        : [...mapping.sources, source],
    });
  }

  async function runImport(): Promise<void> {
    if (events.current === null || mapping === null) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await api.importSkylight(events.current, mapping));
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  async function removeImported(): Promise<void> {
    setRemoving(true);
    setError(null);
    try {
      const { removed } = await api.removeImported();
      setResult(null);
      setError(
        removed === 0
          ? "There was nothing imported to remove."
          : `Removed ${removed} imported event${removed === 1 ? "" : "s"}.`,
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That didn't work.");
    } finally {
      setRemoving(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="screen">
        <header className="week__head week__head--plain">
          <h1>Import</h1>
        </header>
        <p className="note">Only the adults in the household can import a calendar.</p>
      </div>
    );
  }

  const choiceFor = (label: string): string => {
    const rule = mapping?.categories[label];
    if (rule === undefined || rule.skip === true) return SKIP;
    return rule.person ?? SKIP;
  };

  return (
    <div className="screen">
      <header className="week__head week__head--plain">
        <h1>Import from Skylight</h1>
        <Link to="/settings" className="button-quiet">
          Settings
        </Link>
      </header>

      <section className="section">
        <p className="label">The export file</p>
        <p className="note">
          Run the export tool first — it writes <code>skylight-events.json</code>. Nothing is
          written to the calendar until you press Import, and anything imported can be removed
          again in one go.
        </p>
        <input
          type="file"
          accept="application/json,.json"
          className="import__file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void chooseFile(file);
          }}
        />
        {fileName !== null && <p className="note">Reading {fileName}.</p>}
      </section>

      {error !== null && (
        <section className="section">
          <p className="note note--pending">{error}</p>
        </section>
      )}

      {preview !== null && mapping !== null && (
        <>
          <section className="section">
            <p className="label">What would happen</p>
            <div className="kv">
              <span>Events in the file</span>
              <span className="kv__value">{preview.counts.total}</span>
            </div>
            <div className="kv">
              <span>Would import</span>
              <span className="kv__value">{preview.counts.importing}</span>
            </div>
            <div className="kv">
              <span>Would skip</span>
              <span className="kv__value">{preview.counts.skipping}</span>
            </div>
            {preview.per_person.map((entry) => (
              <div className="kv" key={entry.name}>
                <span>{entry.name}</span>
                <span className="kv__value">{entry.count}</span>
              </div>
            ))}
          </section>

          <section className="section">
            <p className="label">Where the events come from</p>
            {preview.sources.map((source) => (
              <label className="import__row" key={source}>
                <input
                  type="checkbox"
                  checked={mapping.sources.includes(source)}
                  onChange={() => toggleSource(source)}
                />
                <span className="import__name">{source}</span>
                <span className="note">{SOURCE_LABELS[source] ?? ""}</span>
              </label>
            ))}
          </section>

          <section className="section">
            <p className="label">Who each Skylight category becomes</p>
            <p className="note">
              Skylight files events under categories, and that list holds people, a synced
              account&rsquo;s address and a holiday feed all together. Frankie has guessed;
              correct anything it got wrong. <strong>Home</strong> is the house itself — bin day,
              the cleaner, the landscaper.
            </p>

            {Object.keys(mapping.categories).map((label) => {
              const undecided = preview.unresolved.includes(label);
              return (
                <div className={`import__row${undecided ? " is-undecided" : ""}`} key={label}>
                  <span className="import__name">{label}</span>
                  <select
                    className="import__select"
                    value={choiceFor(label)}
                    onChange={(e) => setRule(label, e.target.value)}
                  >
                    <option value={SKIP}>Don&rsquo;t import</option>
                    {names.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <label className="import__shade">
                    <input
                      type="checkbox"
                      checked={mapping.categories[label]?.background === true}
                      onChange={() => toggleBackground(label)}
                    />
                    <span>Quiet</span>
                  </label>
                  {mapping.categories[label]?.note !== undefined && (
                    <span className="note">{mapping.categories[label]?.note}</span>
                  )}
                </div>
              );
            })}
          </section>

          {preview.suspicious.length > 0 && (
            <section className="section">
              <p className="label">Worth a look first</p>
              <p className="note">
                These came out of Skylight with times that don&rsquo;t look right. Frankie
                imports them exactly as Skylight has them rather than guessing at what was
                meant — fix them in Skylight and import again, or correct them here afterwards.
              </p>
              {preview.suspicious.map((item, index) => (
                <div className="import__sample" key={`${item.title}-${index}`}>
                  <span className="import__when">
                    {String(item.starts_at).slice(0, 16).replace("T", " ")}
                  </span>
                  <span className="import__title">{item.title}</span>
                  <span className="import__who">{item.reason}</span>
                </div>
              ))}
            </section>
          )}

          {preview.skipped_reasons.length > 0 && (
            <section className="section">
              <p className="label">Skipped</p>
              {preview.skipped_reasons.map((entry) => (
                <div className="kv" key={entry.reason}>
                  <span>{entry.reason}</span>
                  <span className="kv__value">{entry.count}</span>
                </div>
              ))}
            </section>
          )}

          <section className="section">
            <p className="label">The first {preview.sample.length} that would land</p>
            {preview.sample.map((item, index) => (
              <div className="import__sample" key={`${item.title}-${index}`}>
                <span className="import__when">
                  {/* The end matters here as much as the start: a week in
                      Denver that arrives as one Tuesday is exactly the kind
                      of mapping mistake this preview exists to catch. */}
                  {item.all_day
                    ? `${String(item.starts_at).slice(0, 10)}${
                        item.ends !== null && item.ends.slice(0, 10) !== String(item.starts_at).slice(0, 10)
                          ? ` → ${item.ends.slice(0, 10)}`
                          : ""
                      } all day`
                    : `${String(item.starts_at).slice(0, 16).replace("T", " ")}${
                        item.ends !== null && item.ends.slice(0, 10) !== String(item.starts_at).slice(0, 10)
                          ? ` → ${item.ends.slice(0, 16).replace("T", " ")}`
                          : ""
                      }`}
                </span>
                <span className="import__title">
                  {item.title}
                  {item.rrule !== null && <span className="import__repeat"> repeats</span>}
                  {item.background && <span className="import__repeat"> quiet</span>}
                </span>
                <span className="import__who">{item.people.join(", ")}</span>
              </div>
            ))}
          </section>

          <div className="buttons">
            <button
              type="button"
              className="button-ink"
              disabled={busy || preview.counts.importing === 0}
              onClick={() => void runImport()}
            >
              {busy ? "Working…" : `Import ${preview.counts.importing} events`}
            </button>
          </div>
        </>
      )}

      {result !== null && (
        <section className="section">
          <p className="label">Done</p>
          <div className="kv">
            <span>Added</span>
            <span className="kv__value">{result.inserted}</span>
          </div>
          <div className="kv">
            <span>Updated</span>
            <span className="kv__value">{result.updated}</span>
          </div>
          {result.failed.length > 0 && (
            <>
              <p className="note note--pending">{result.failed.length} didn&rsquo;t make it:</p>
              {result.failed.slice(0, 10).map((failure) => (
                <p className="note" key={failure.title}>
                  {failure.title} — {failure.error}
                </p>
              ))}
            </>
          )}
          <p className="note">
            Run it again any time — it corrects what it already brought in rather than adding a
            second copy.
          </p>
        </section>
      )}

      <section className="section">
        <p className="label">Undo</p>
        <p className="note">
          Removes every event that came from Skylight and nothing else. Anything typed into
          Frankie by hand is untouched, so this is safe to use if the import came out wrong.
        </p>
        <button
          type="button"
          className="danger"
          disabled={removing}
          onClick={() => void removeImported()}
        >
          {removing ? "Removing…" : "Remove imported events"}
        </button>
      </section>
    </div>
  );
}
